import React, { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import {
  Clock, Users, Award, CheckCircle, ArrowLeft,
  BookOpen, Target, GraduationCap, Calendar,
  DollarSign, ChevronDown, ChevronUp, AlertCircle, Mail, Package, Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { coursesData } from '@/data/courses/index';
import BundleCallout from '../components/courses/BundleCallout';
import CourseRegisterForm from '@/components/courses/CourseRegisterForm';

const CoursePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [isEnrolling, setIsEnrolling] = useState(false);
  const [quantity, setQuantity] = useState(1);
  // Pre-fill the promo code from ?promo= so an ad that says "free with code
  // AIFREE26" does not make the visitor hunt for a collapsed field and retype
  // it. Without this the entry offer of the whole funnel costs the user four
  // extra interactions on a page that first shows them a price. (#317)
  // Sanitised to the character class Stripe promotion codes use, and capped at
  // the same 50 chars as the manual input — this value is attacker-controlled
  // via the URL and is echoed into the DOM.
  const promoParam = (() => {
    if (typeof window === 'undefined') return '';
    const raw = new URLSearchParams(window.location.search).get('promo') || '';
    return raw.toUpperCase().replace(/[^A-Z0-9_-]/g, '').slice(0, 50);
  })();

  const [promoCode, setPromoCode] = useState(promoParam);
  const [showPromo, setShowPromo] = useState(Boolean(promoParam));

  const slugParam = typeof slug === 'string' ? slug : '';

  // Newsletter form state
  const [newsletterForm, setNewsletterForm] = useState({
    name: '',
    email: '',
    course: slugParam // Pre-select current course
  });
  const [isNewsletterSubmitting, setIsNewsletterSubmitting] = useState(false);

  const course = Object.prototype.hasOwnProperty.call(coursesData, slugParam)
    ? coursesData[slugParam]
    : undefined;

  // Dynamic course options for dropdown
  const courseOptions = useMemo(() => {
    const allCourses = Object.values(coursesData).map(c => ({
      value: c.slug,
      label: c.title
    }));

    // Sort alphabetically and add "General Updates" option
    allCourses.sort((a, b) => a.label.localeCompare(b.label));

    return [
      ...allCourses,
      { value: 'general', label: 'General Updates (All Courses)' }
    ];
  }, []);

  // Defensive: remove policy links that may be injected by external content.
  // Declared before the not-found early return so hook order stays stable when
  // the slug changes from a valid course to an unknown one.
  useEffect(() => {
    const removePolicyLinks = () => {
      const selectors = [
        'a[href="/terms-of-service"]',
        'a[href="/privacy-policy"]',
        'a[href="/refund-policy"]',
        'a[href*="terms"i]',
        'a[href*="privacy"i]',
        'a[href*="refund"i]'
      ];
      selectors.forEach(sel => {
        document.querySelectorAll(sel).forEach(el => el.remove());
      });
    };

    // Run once on mount and also after short delay in case content is injected later
    removePolicyLinks();
    const t = setTimeout(removePolicyLinks, 1000);
    return () => clearTimeout(t);
  }, []);

  if (!course) {
    return (
      <div className="pt-24 pb-20 bg-white min-h-screen">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-4xl font-bold mb-4">Course Not Found</h1>
          <p className="text-gray-600 mb-8">The course you're looking for doesn't exist.</p>
          <Button onClick={() => navigate('/training')}>
            <ArrowLeft className="mr-2" size={16} />
            Back to Catalog
          </Button>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Pricing model (#325)
  //
  // `price.aud`       — the regular list price. The Stripe Price object referenced
  //                     by `stripeConfig.priceId` is created from THIS amount,
  //                     so this is the amount Stripe actually charges at checkout.
  // `price.earlyBird` — the promotional (lower) amount. It is NOT a separate
  //                     Stripe price; it is applied as a coupon / promo code at
  //                     checkout.
  //
  // These two therefore DIVERGE by design: `displayPrice` is the headline we show
  // (early-bird when it exists), while `chargedPrice` is what Stripe bills and is
  // the only figure used for the seat multiplier and the GA4 `begin_checkout`
  // value. Do not collapse them into one variable.
  // ---------------------------------------------------------------------------
  const price = course.price || {};
  const hasEarlyBird =
    typeof price.earlyBird === 'number' &&
    typeof price.aud === 'number' &&
    price.earlyBird < price.aud;
  const displayPrice = hasEarlyBird ? price.earlyBird : price.aud;
  const chargedPrice = price.aud;
  // Stripe object IDs are `price_` followed by base62 — never underscores, never a
  // short human-authored token. Catalogue files carry both empty strings and
  // hand-written stand-ins ('price_tbd', 'price_PLACEHOLDER_...', 'price_sharecop_gov_001'),
  // all of which fail session creation and surface only a generic error toast.
  // Reject them up front and offer the enquiry path instead. (#331)
  const hasStripePrice = /^price_[A-Za-z0-9]{10,}$/.test(course.stripeConfig?.priceId || '');

  // Next dated session, if any. Courses carry `sessions: [{start, end, capacity}]`
  // with ISO offsets; anything already started is past.
  const nextSession = (Array.isArray(course.sessions) ? course.sessions : [])
    .filter((s) => s?.start && new Date(s.start) > new Date())
    .sort((a, b) => new Date(a.start) - new Date(b.start))[0];

  // Session gating is OPT-IN: it applies only to courses that declare a
  // `sessions` array. A course that has not adopted the model yet keeps its
  // previous behaviour and stays sellable.
  //
  // This matters more than it looks. Gating every course on having a session
  // suppressed Enrol across the whole catalogue — 47 courses with perfectly
  // good Stripe prices became unbuyable, because only the four new ones carry
  // `sessions`. Opt-in confines the change to courses we have actually dated.
  //
  // Where a course DOES declare sessions, a future one is required: "September
  // 2026, Tuesdays" identifies four or five candidate dates, so taking $395
  // against it is not a supply commitment under Australian Consumer Law. Those
  // courses offer the waitlist instead. (#341)
  const declaresSessions = Array.isArray(course.sessions);
  const canEnrol = hasStripePrice && (!declaresSessions || Boolean(nextSession));

  const sessionLabel = nextSession
    ? new Date(nextSession.start).toLocaleString('en-AU', {
        weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
        hour: 'numeric', minute: '2-digit', timeZone: 'Australia/Sydney',
      })
    : null;

  // Promo badge — suppress once expired so a stale offer is never advertised.
  const promo = course.promo;
  const promoIsLive =
    Boolean(promo?.code) &&
    (!promo.expires || new Date(`${promo.expires}T23:59:59`) >= new Date());

  const formatIncludes = Array.isArray(course.format?.includes) ? course.format.includes : [];
  const outcomes = Array.isArray(course.outcomes) ? course.outcomes : [];
  const modules = Array.isArray(course.modules) ? course.modules : [];
  const audience = Array.isArray(course.audience) ? course.audience : [];
  const prerequisites = Array.isArray(course.prerequisites) ? course.prerequisites : [];
  const faqs = Array.isArray(course.faqs) ? course.faqs : [];
  const logisticsRequirements = Array.isArray(course.logistics?.requirements)
    ? course.logistics.requirements
    : [];
  const hasDeliverySection =
    Boolean(course.format?.type) ||
    Boolean(course.logistics?.classSize) ||
    logisticsRequirements.length > 0 ||
    formatIncludes.length > 0;

  const handleEnrollment = async () => {
    if (!canEnrol) {
      toast({
        title: "Online Enrolment Unavailable",
        description: "This course isn't set up for online checkout yet. Please contact us and we'll book your seat.",
        variant: "destructive"
      });
      return;
    }

    setIsEnrolling(true);

    try {
      // Call Cloudflare Function to create Stripe checkout session
      const response = await fetch('/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          courseId: course.slug,
          priceId: course.stripeConfig.priceId,
          courseTitle: course.title,
          quantity: quantity > 0 ? quantity : 1,
          couponCode: promoCode.trim() || undefined,
          successUrl: `${window.location.origin}/training/${course.slug}/success`,
          cancelUrl: `${window.location.origin}/training/${course.slug}/cancel`
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create checkout session');
      }

      const data = await response.json();

      // Track checkout start (GA4).
      // Uses `chargedPrice` (= price.aud) deliberately — that is the amount the
      // Stripe Price bills. Any early-bird / promo reduction is a coupon applied
      // inside Checkout and is reflected in the server-side purchase event, not here.
      if (window.gtag && typeof chargedPrice === 'number') {
        window.gtag('event', 'begin_checkout', {
          currency: price.currency || 'AUD',
          value: chargedPrice,
          items: [{
            item_id: course.slug,
            item_name: course.title,
            price: chargedPrice,
            quantity: 1,
          }]
        });
      }

      // Redirect to Stripe Checkout
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL received');
      }

    } catch (error) {
      console.error('Enrollment error:', error);
      toast({
        title: "Enrollment Error",
        description: "Unable to process enrollment. Please try again or contact support.",
        variant: "destructive"
      });
      setIsEnrolling(false);
    }
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();

    // Form validation
    if (!newsletterForm.name.trim()) {
      toast({
        title: "Name Required",
        description: "Please enter your name to continue.",
        variant: "destructive"
      });
      return;
    }

    if (!newsletterForm.email.trim()) {
      toast({
        title: "Email Required",
        description: "Please enter your email address to continue.",
        variant: "destructive"
      });
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newsletterForm.email.trim())) {
      toast({
        title: "Invalid Email",
        description: "Please enter a valid email address.",
        variant: "destructive"
      });
      return;
    }

    setIsNewsletterSubmitting(true);

    try {
      const response = await fetch('/newsletter', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newsletterForm.name.trim(),
          email: newsletterForm.email.trim(),
          course: newsletterForm.course || slug
        })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        toast({
          title: "Check Your Email!",
          description: "We've sent a confirmation email. Please verify your subscription to start receiving updates.",
          duration: 6000
        });
        // Reset form
        setNewsletterForm({
          name: '',
          email: '',
          course: slug || ''
        });
      } else {
        toast({
          title: "Subscription Error",
          description: result.message || "Unable to subscribe. Please try again later.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      toast({
        title: "Network Error",
        description: "An unexpected error occurred. Please check your connection and try again.",
        variant: "destructive"
      });
    } finally {
      setIsNewsletterSubmitting(false);
    }
  };

  const handleContactClick = () => {
    navigate('/contact', {
      state: {
        inquiryType: 'Training',
        course: course.title,
        source: `/training/${slug}`,
        message: `I'm interested in the ${course.title} course. Please contact me with more information.`
      }
    });
  };

  // Social preview card. Falls back to the site-wide 1200x630 card rather than
  // the logo, so a shared or advertised course link always renders a full-width
  // preview. Course cards are generated into public/og/courses/<slug>.jpg; set
  // `ogImage` on the course to override.
  const ogImage = course.ogImage
    ? `https://educ4te.com${course.ogImage}`
    : 'https://educ4te.com/og-image.jpg';

  return (
    <>
      <Helmet>
        {/* Single-expression child. react-helmet-async passes <title> children
            straight through to React 19's hoisting path, which silently renders
            an EMPTY <title> when children is an array (e.g. `{expr} - literal`).
            Always interpolate the whole string in one expression. (#330) */}
        <title>{`${course.title} - Educ4te Training`}</title>
        <meta name="description" content={course.shortDescription} />
        <meta name="keywords" content={`${course.title}, cybersecurity training Australia, ${course.level || ''} course, Microsoft security, ACSC compliance`} />
        <link rel="canonical" href={`https://educ4te.com/training/${course.slug}`} />
        <meta property="og:title" content={`${course.title} - Educ4te Training`} />
        <meta property="og:description" content={course.shortDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en_AU" />
        <meta property="og:url" content={`https://educ4te.com/training/${course.slug}`} />
        {/* Social cards need a 1200x630 image. The logo was being served here,
            which renders as a small square thumbnail under a paid ad and reads
            as a broken preview. Per-course cards drop in via `course.ogImage`,
            the same convention the assessment-tool configs already use. The
            fallback is the real 1200x630 site card, never the logo. (#319) */}
        <meta property="og:image" content={ogImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content={`${course.title} — EDUC4TE training`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${course.title} - Educ4te Training`} />
        <meta name="twitter:description" content={course.shortDescription} />
        <meta name="twitter:image" content={ogImage} />
        <meta name="twitter:image:alt" content={`${course.title} — EDUC4TE training`} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Course",
          "name": course.title,
          "description": course.overview,
          "provider": {
            "@type": "Organization",
            "name": "Educ4te",
            "url": "https://educ4te.com"
          },
          "inLanguage": "en-AU",
          "educationalLevel": course.level,
          "courseWorkload": course.duration,
          "hasCourseInstance": {
            "@type": "CourseInstance",
            "courseMode": "online",
            "inLanguage": "en-AU",
            ...(course.price?.aud ? {
              "offers": {
                "@type": "Offer",
                "price": course.price.aud,
                "priceCurrency": "AUD"
              }
            } : {})
          }
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {"@type": "ListItem", "position": 1, "name": "Home", "item": "https://educ4te.com/"},
            {"@type": "ListItem", "position": 2, "name": "Training", "item": "https://educ4te.com/training"},
            {"@type": "ListItem", "position": 3, "name": course.title, "item": `https://educ4te.com/training/${course.slug}`}
          ]
        })}</script>
      </Helmet>

      <div className="pt-24 pb-20 bg-white min-h-screen">
        {/* Back Button */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/training')}
            className="text-gray-600 hover:text-[#422680]"
          >
            <ArrowLeft className="mr-2" size={16} />
            Back to Catalog
          </Button>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2">
              {/* Hero Section */}
              <div className="mb-8">
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <span className="inline-block bg-[#F54952] text-white text-sm font-bold px-4 py-1 rounded-full">
                    {course.level}
                  </span>
                  {course.courseCode && (
                    <span className="inline-block bg-[#422680] text-white text-xs font-mono font-bold px-3 py-1 rounded-full tracking-wide">
                      {course.courseCode}
                    </span>
                  )}
                  {promoIsLive && (
                    <span className="inline-block bg-amber-400 text-[#422680] text-xs font-bold px-3 py-1 rounded-full">
                      {promo.label}: {promo.percentOff}% off — code {promo.code}
                    </span>
                  )}
                </div>
                <h1 className="text-4xl md:text-5xl font-bold mb-4">
                  <span className="bg-gradient-to-r from-[#422680] via-[#660F56] to-[#F54952] bg-clip-text text-transparent">
                    {course.title}
                  </span>
                </h1>
                <p className="text-xl text-gray-600 mb-4">{course.subtitle}</p>
                <p className="text-lg text-gray-700 mb-6">{course.valueProposition}</p>
                
                <div className="flex flex-wrap gap-6 mb-6">
                  <div className="flex items-center gap-2 text-gray-700">
                    <Clock size={20} className="text-[#422680]" />
                    <span className="font-medium">{course.duration}</span>
                  </div>
                  {course.students && (
                    <div className="flex items-center gap-2 text-gray-700">
                      <Users size={20} className="text-[#422680]" />
                      {/* `students` is either a count ("100+") or the literal
                          "New". Appending " enrolled" unconditionally rendered
                          "New enrolled" — which is now every course in the
                          catalogue, since the invented counts were removed. */}
                      <span className="font-medium">
                        {course.students === 'New' ? 'New course' : `${course.students} enrolled`}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-gray-700">
                    <Award size={20} className="text-[#422680]" />
                    <span className="font-medium">Certificate Included</span>
                  </div>
                </div>

                {course.image && (
                  <img
                    src={course.image}
                    alt={course.title}
                    className="w-full h-64 object-cover rounded-2xl shadow-lg"
                  />
                )}
              </div>

              {/* Key Deliverable highlight */}
              {course.keyDeliverable && (
                <section className="mb-8 bg-gradient-to-br from-[#F4F0FA] to-white border-l-4 border-[#422680] rounded-xl p-6">
                  <div className="flex items-start gap-3">
                    <Award className="text-[#422680] flex-shrink-0 mt-1" size={24} />
                    <div>
                      <h3 className="text-lg font-bold text-[#341671] mb-1">
                        Primary deliverable: {course.keyDeliverable.title}
                      </h3>
                      <p className="text-sm text-gray-700 leading-relaxed">
                        {course.keyDeliverable.description}
                      </p>
                    </div>
                  </div>
                </section>
              )}

              {/* Series callout */}
              {course.series && (
                <section className="mb-12 bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                  <div className="flex items-start gap-3 mb-3">
                    <Package className="text-[#422680] flex-shrink-0 mt-1" size={20} />
                    <div>
                      <h3 className="text-base font-bold text-[#341671]">
                        Part of the {course.series.name}
                      </h3>
                      <p className="text-xs text-gray-500 mt-0.5">
                        This course: <span className="font-semibold text-[#422680]">{course.series.position}</span>
                      </p>
                    </div>
                  </div>
                  {course.series.description && (
                    <p className="text-sm text-gray-700 mb-4 leading-relaxed">
                      {course.series.description}
                    </p>
                  )}
                  <ul className="space-y-2">
                    {course.series.courses?.map((sibling) => {
                      const isCurrent = sibling.slug === course.slug;
                      const inner = (
                        <span className={`flex items-start gap-2 text-sm ${isCurrent ? 'font-semibold text-[#422680]' : 'text-gray-700'}`}>
                          <span className="font-mono text-xs text-gray-500 mt-0.5 min-w-[140px]">{sibling.code}</span>
                          <span>{sibling.title}{isCurrent && ' — you are here'}</span>
                        </span>
                      );
                      return (
                        <li key={sibling.code}>
                          {sibling.slug && !isCurrent ? (
                            <Link to={`/training/${sibling.slug}`} className="hover:text-[#660F56] hover:underline">
                              {inner}
                            </Link>
                          ) : (
                            inner
                          )}
                        </li>
                      );
                    })}
                  </ul>
                  {course.series.contact && (
                    <p className="text-xs text-gray-500 mt-4 pt-3 border-t border-gray-100">
                      In-house delivery and bundle enquiries: <a href={`mailto:${course.series.contact}`} className="text-[#422680] hover:underline">{course.series.contact}</a>
                    </p>
                  )}
                </section>
              )}

              {/* Bundle callout (renders only when course.series.bundle exists) */}
              {course.series?.bundle && (
                <BundleCallout bundle={course.series.bundle} />
              )}

              {/* Course Snapshot */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold mb-6">At a Glance</h2>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-gray-50 rounded-xl p-6">
                    <h3 className="font-bold text-gray-900 mb-3">Who it's for</h3>
                    <ul className="space-y-2">
                      {course.snapshot?.whoItsFor?.map((item, index) => (
                        <li key={index} className="text-gray-700 text-sm flex items-start gap-2">
                          <CheckCircle className="text-[#422680] flex-shrink-0 mt-0.5" size={16} />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-6">
                    <h3 className="font-bold text-gray-900 mb-3">Course Details</h3>
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Duration:</span>
                        <span className="font-medium text-gray-900 min-w-[140px] text-right">{course.duration}</span>
                      </div>
                      {course.format?.type && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Format:</span>
                          <span className="font-medium text-gray-900 min-w-[140px] text-right">{course.format.type}</span>
                        </div>
                      )}
                      {course.nextIntake?.date && (
                        <div className="flex justify-between">
                          {/* `nextIntake.date` holds the date + CTA only — the
                              "Next intake:" wording lives here, never in the data (#328). */}
                          <span className="text-gray-600">Next intake:</span>
                          <span className="font-medium text-gray-900 min-w-[140px] text-right">{course.nextIntake.date}</span>
                        </div>
                      )}
                      {course.snapshot?.accreditation && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Alignment:</span>
                          <span className="font-medium text-gray-900 min-w-[140px] text-right">{course.snapshot.accreditation}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </section>

              {/* Overview */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold mb-4 flex items-center gap-2">
                  <BookOpen className="text-[#422680]" size={28} />
                  Course Overview
                </h2>
                <p className="text-gray-700 text-lg leading-relaxed">{course.overview}</p>
              </section>

              {/* Learning Outcomes */}
              {outcomes.length > 0 && (
              <section className="mb-12">
                <h2 className="text-3xl font-bold mb-6 flex items-center gap-2">
                  <Target className="text-[#422680]" size={28} />
                  What You'll Learn
                </h2>
                <div className="grid md:grid-cols-2 gap-4">
                  {outcomes.map((outcome, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <CheckCircle className="text-green-600 flex-shrink-0 mt-1" size={20} />
                      <span className="text-gray-700">{outcome}</span>
                    </div>
                  ))}
                </div>
              </section>
              )}

              {/* Course Modules */}
              {modules.length > 0 && (
              <section className="mb-12">
                <h2 className="text-3xl font-bold mb-6">Course Curriculum</h2>
                <div className="space-y-4">
                  {modules.map((module, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="bg-gray-50 rounded-xl p-6 border border-gray-100 hover:border-[#422680] transition-colors"
                    >
                      <div className="flex justify-between items-start mb-3">
                        <h3 className="text-xl font-bold text-gray-900">
                          Module {index + 1}: {module.title}
                        </h3>
                        <span className="text-sm text-gray-600 font-medium bg-white px-3 py-1 rounded-full">
                          {module.duration}
                        </span>
                      </div>
                      <ul className="space-y-2">
                        {module.topics?.map((topic, topicIndex) => (
                          <li key={topicIndex} className="flex items-start gap-2 text-gray-700">
                            <CheckCircle className="text-[#422680] flex-shrink-0 mt-1" size={16} />
                            <span>{topic}</span>
                          </li>
                        ))}
                      </ul>
                    </motion.div>
                  ))}
                </div>
              </section>
              )}

              {/* Target Audience */}
              {audience.length > 0 && (
              <section className="mb-12">
                <h2 className="text-3xl font-bold mb-6 flex items-center gap-2">
                  <GraduationCap className="text-[#422680]" size={28} />
                  Who Should Attend
                </h2>
                <div className="bg-gradient-to-br from-[#422680]/5 to-[#F54952]/5 rounded-xl p-6">
                  <ul className="space-y-3">
                    {audience.map((person, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle className="text-[#422680] flex-shrink-0 mt-1" size={20} />
                        <span className="text-gray-700 font-medium">{person}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </section>
              )}

              {/* Prerequisites */}
              {prerequisites.length > 0 && (
              <section className="mb-12">
                <h2 className="text-3xl font-bold mb-6">Prerequisites</h2>
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                  <div className="flex items-start gap-3 mb-4">
                    <AlertCircle className="text-blue-600 flex-shrink-0" size={24} />
                    <p className="text-blue-900 font-medium">
                      Before enrolling, please ensure you meet these requirements:
                    </p>
                  </div>
                  <ul className="space-y-2 ml-9">
                    {prerequisites.map((prereq, index) => (
                      <li key={index} className="text-blue-800">• {prereq}</li>
                    ))}
                  </ul>
                </div>
              </section>
              )}

              {/* Delivery, Format and Logistics */}
              {hasDeliverySection && (
              <section className="mb-12">
                <h2 className="text-3xl font-bold mb-6 flex items-center gap-2">
                  <Package className="text-[#422680]" size={28} />
                  Delivery, Format and Logistics
                </h2>
                <div className="bg-gradient-to-br from-[#422680]/5 to-[#F54952]/5 rounded-xl p-6">
                  <div className="space-y-6">
                    {(course.format?.type || course.logistics?.classSize) && (
                      <div>
                        <h3 className="font-bold text-gray-900 mb-2">Delivery Mode</h3>
                        {course.format?.type && <p className="text-gray-700">{course.format.type}</p>}
                        {course.logistics?.classSize && (
                          <p className="text-gray-700 mt-1">{course.logistics.classSize}</p>
                        )}
                      </div>
                    )}
                    {logisticsRequirements.length > 0 && (
                      <div>
                        <h3 className="font-bold text-gray-900 mb-2">What You'll Need</h3>
                        <ul className="space-y-1">
                          {logisticsRequirements.map((req, index) => (
                            <li key={index} className="text-gray-700 flex items-start gap-2">
                              <CheckCircle className="text-[#422680] flex-shrink-0 mt-1" size={16} />
                              <span>{req}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {formatIncludes.length > 0 && (
                      <div>
                        <h3 className="font-bold text-gray-900 mb-2">What You'll Receive</h3>
                        <ul className="space-y-1">
                          {formatIncludes.map((item, index) => (
                            <li key={index} className="text-gray-700 flex items-start gap-2">
                              <CheckCircle className="text-green-600 flex-shrink-0 mt-1" size={16} />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </section>
              )}

              {/* FAQs */}
              {faqs.length > 0 && (
              <section className="mb-12">
                <h2 className="text-3xl font-bold mb-6">Frequently Asked Questions</h2>
                <div className="space-y-4">
                  {faqs.map((faq, index) => (
                    <div 
                      key={index}
                      className="bg-white border border-gray-200 rounded-xl overflow-hidden"
                    >
                      <button
                        onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                        className="w-full flex justify-between items-center p-6 text-left hover:bg-gray-50 transition-colors"
                      >
                        <span className="font-bold text-gray-900 pr-4">{faq.question}</span>
                        {expandedFaq === index ? (
                          <ChevronUp className="text-[#422680] flex-shrink-0" size={24} />
                        ) : (
                          <ChevronDown className="text-gray-400 flex-shrink-0" size={24} />
                        )}
                      </button>
                      {expandedFaq === index && (
                        <div className="px-6 pb-6 text-gray-700">
                          {faq.answer}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>
              )}

              {/* Newsletter Subscription */}
              <section className="mb-12">
                <div className="bg-gradient-to-br from-[#422680]/10 to-[#F54952]/10 rounded-2xl p-8">
                  <div className="flex items-start gap-4 mb-6">
                    <Mail className="text-[#422680] flex-shrink-0" size={32} />
                    <div>
                      <h2 className="text-2xl font-bold mb-2">Not Ready to Enrol?</h2>
                      <p className="text-gray-700">
                        Join our mailing list to receive updates about courses, resources, and cybersecurity insights tailored for Australian organisations.
                      </p>
                    </div>
                  </div>

                  <form onSubmit={handleNewsletterSubmit} className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="newsletter-name" className="block text-sm font-medium text-gray-700 mb-1">
                          Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          id="newsletter-name"
                          value={newsletterForm.name}
                          onChange={(e) => setNewsletterForm({ ...newsletterForm, name: e.target.value })}
                          placeholder="Your full name"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#422680] focus:border-transparent"
                          disabled={isNewsletterSubmitting}
                        />
                      </div>
                      <div>
                        <label htmlFor="newsletter-email" className="block text-sm font-medium text-gray-700 mb-1">
                          Email Address <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          id="newsletter-email"
                          value={newsletterForm.email}
                          onChange={(e) => setNewsletterForm({ ...newsletterForm, email: e.target.value })}
                          placeholder="your.email@example.com"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#422680] focus:border-transparent"
                          disabled={isNewsletterSubmitting}
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="newsletter-course" className="block text-sm font-medium text-gray-700 mb-1">
                        Course Interest
                      </label>
                      <select
                        id="newsletter-course"
                        value={newsletterForm.course}
                        onChange={(e) => setNewsletterForm({ ...newsletterForm, course: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#422680] focus:border-transparent bg-white"
                        disabled={isNewsletterSubmitting}
                      >
                        {courseOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.value === slug ? `${option.label} (Current)` : option.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-4 items-start pt-2">
                      <Button
                        type="submit"
                        disabled={isNewsletterSubmitting}
                        className="bg-[#422680] hover:bg-[#660F56] text-white"
                      >
                        {isNewsletterSubmitting ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Subscribing...
                          </>
                        ) : (
                          <>
                            <Mail className="mr-2 h-4 w-4" />
                            Send Me Updates
                          </>
                        )}
                      </Button>
                      <Button onClick={handleContactClick} variant="outline" type="button" className="w-full sm:w-auto flex-shrink-0">
                        Contact Us
                      </Button>
                    </div>

                    <p className="text-xs text-gray-600">
                      By subscribing, you agree to receive email updates from Educ4te. You can unsubscribe at any time.
                      We respect your privacy and will never share your information.
                    </p>
                  </form>
                </div>
              </section>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24">
                {/* Enrollment Card */}
                <div className="bg-gradient-to-br from-[#422680] to-[#660F56] rounded-2xl p-8 text-white shadow-2xl mb-6">
                  {typeof displayPrice === 'number' && (
                    <div className="mb-6">
                      <div className="flex items-baseline gap-2 mb-2">
                        <span className="text-5xl font-bold">${displayPrice}</span>
                        <span className="text-xl text-white/80">{price.currency || 'AUD'}</span>
                      </div>
                      {hasEarlyBird && (
                        <div className="text-white/90">
                          {/* earlyBird is the LOWER amount, so it is the headline and the
                              regular list price is what gets struck through (#325). */}
                          <span className="line-through text-white/60">${price.aud}</span>
                          <span className="ml-2 bg-[#F54952] text-white text-xs font-bold px-2 py-1 rounded">
                            EARLY BIRD
                          </span>
                        </div>
                      )}
                      {/* One instruction only. Where a course carries both an
                          `earlyBird` amount and a `promo` block they are usually the
                          SAME offer stated twice (e.g. aud 395 / earlyBird 296 is
                          exactly the 25% AISEP26 promo), so showing a generic
                          early-bird caption alongside the promo badge reads as two
                          stackable discounts. Prefer the authored promo note. */}
                      {promoIsLive && promo.note ? (
                        <p className="text-xs text-white/70 mt-2">{promo.note}</p>
                      ) : hasEarlyBird ? (
                        <p className="text-xs text-white/70 mt-2">
                          Early-bird rate — apply your promo code at checkout.
                        </p>
                      ) : null}
                    </div>
                  )}

                  {/* Quantity picker */}
                  <div className="mb-4">
                    <label className="block text-xs text-white/80 mb-1">Number of seats</label>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        aria-label="Decrease quantity"
                        onClick={() => setQuantity(q => Math.max(1, q - 1))}
                        className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 text-white font-bold flex items-center justify-center"
                      >−</button>
                      <span className="w-8 text-center font-semibold">{quantity}</span>
                      <button
                        type="button"
                        aria-label="Increase quantity"
                        onClick={() => setQuantity(q => q + 1)}
                        className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 text-white font-bold flex items-center justify-center"
                      >+</button>
                      {quantity > 1 && typeof chargedPrice === 'number' && (
                        <span className="text-xs text-white/80 ml-1">
                          {/* Multiplies the CHARGED price (price.aud), not the early-bird
                              headline — Stripe bills price.aud per seat and applies any
                              early-bird discount as a coupon inside Checkout. */}
                          = AUD ${(chargedPrice * quantity).toLocaleString()}
                        </span>
                      )}
                    </div>
                    {quantity >= 3 && (
                      <p className="text-xs text-yellow-300 mt-1">Group booking — contact us for volume discount</p>
                    )}
                  </div>

                  {/* Promo code */}
                  {canEnrol && (
                  <div className="mb-4">
                    <button
                      type="button"
                      onClick={() => setShowPromo(p => !p)}
                      className="text-xs text-white/70 hover:text-white underline underline-offset-2 flex items-center gap-1"
                    >
                      {showPromo ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                      {showPromo ? 'Hide promo code' : 'Have a promo code?'}
                    </button>
                    {showPromo && (
                      <input
                        type="text"
                        value={promoCode}
                        onChange={e => setPromoCode(e.target.value)}
                        placeholder="Enter code"
                        className="mt-2 w-full rounded-lg bg-white/20 border border-white/30 px-3 py-2 text-sm text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/50"
                        maxLength={50}
                      />
                    )}
                  </div>
                  )}

                  {canEnrol ? (
                    <>
                      <Button
                        id="enrol-now-button"
                        onClick={handleEnrollment}
                        disabled={isEnrolling}
                        className="w-full bg-white text-[#422680] hover:bg-gray-100 font-bold text-lg py-6 mb-4"
                      >
                        {isEnrolling ? 'Processing...' : quantity > 1 ? `Enrol ${quantity} Seats` : 'Enrol Now'}
                      </Button>

                      <p className="text-sm text-white/80 text-center mb-6">
                        Secure payment via Stripe · {promoCode.trim() ? 'Promo applied at checkout' : 'Promo codes accepted'}
                      </p>
                    </>
                  ) : (
                    <>
                      {/* Either no Stripe price (#331) or no dated session (#341).
                          Never post an empty priceId, and never sell a seat we
                          cannot put a date against. */}
                      <Button
                        id="enrol-now-button"
                        onClick={handleContactClick}
                        className="w-full bg-white text-[#422680] hover:bg-gray-100 font-bold text-lg py-6 mb-4"
                      >
                        Enquire About This Course
                      </Button>

                      <p className="text-sm text-white/80 text-center mb-6">
                        {hasStripePrice
                          ? "Dates for the next intake are being confirmed — register your interest below and we'll email you as soon as they're set."
                          : "Online checkout isn't available for this course yet — contact us and we'll book your seat."}
                      </p>
                    </>
                  )}


                  {(course.nextIntake?.date || course.format?.type) && (
                    <div className="space-y-3 pt-6 border-t border-white/20">
                      {course.nextIntake?.date && (
                        <div className="flex items-center gap-3">
                          <Calendar className="flex-shrink-0" size={20} />
                          <div>
                            {/* Heading supplies the "Next Intake" wording; the data value is
                                the date + CTA only. (#328)
                                Where a real dated session exists, show that instead — someone
                                about to pay should see which evening they are paying for. (#341) */}
                            <p className="font-semibold text-sm">Next Intake</p>
                            <p className="text-sm text-white/90">{sessionLabel || course.nextIntake.date}</p>
                            {sessionLabel && course.sessions.length > 1 && (
                              <p className="text-xs text-white/70 mt-1">
                                {course.sessions.length} dates scheduled this cohort
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                      {course.format?.type && (
                        <div className="flex items-center gap-3">
                          <BookOpen className="flex-shrink-0" size={20} />
                          <div>
                            <p className="font-semibold text-sm">Format</p>
                            <p className="text-sm text-white/90">{course.format.type}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="mt-6 pt-6 border-t border-white/20">
                    <p className="text-sm text-white/90 mb-2">Group & Enterprise Options</p>
                    <p className="text-xs text-white/80">
                      Discounted rates available for teams of 3+ delegates. Contact us for in-house delivery options.
                    </p>
                  </div>
                </div>

                {/* Cohort registration (#324). Sibling of the enrolment card, not nested
                    inside it — the form carries its own gradient header and would read as
                    a card-in-card against the purple enrolment panel.
                    Gated on `course.waitlist`, which is deliberately separate from `promo`:
                    promo blocks are deleted when a campaign ends, and keying off them would
                    silently remove the waitlist too. Cohort comes from the data, never
                    hardcoded here.
                    Self-contained — brings its own attribution hook, toast and success
                    state. Its consent copy is deliberately anchor-free because the sweep in
                    the effect above strips every terms/privacy/refund anchor on the page,
                    including ones rendered by children. */}
                {course.waitlist && (
                  <div className="mb-6">
                    <CourseRegisterForm
                      courseSlug={course.slug}
                      courseTitle={course.title}
                      intent={
                        price.aud === 0 || promo?.percentOff === 100
                          ? 'free-enrolment'
                          : 'waitlist'
                      }
                      cohort={course.waitlist.cohort}
                    />
                  </div>
                )}

                {/* What's Included */}
                {formatIncludes.filter((item) => !/money[- ]?back/i.test(item)).length > 0 && (
                  <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-md">
                    <h3 className="text-xl font-bold mb-4 text-gray-900">What's Included</h3>
                    <ul className="space-y-3">
                      {formatIncludes
                        .filter((item) => !/money[- ]?back/i.test(item))
                        .map((item, index) => (
                          <li key={index} className="flex items-start gap-2 text-gray-700">
                            <CheckCircle className="text-green-600 flex-shrink-0 mt-1" size={18} />
                            <span className="text-sm">{item}</span>
                          </li>
                        ))}
                    </ul>
                  </div>
                )}

                {/* Contact Support */}
                <div className="bg-gray-50 rounded-2xl p-6 mt-6 text-center">
                  <p className="text-gray-700 mb-3">Have questions about this course?</p>
                  <Button onClick={handleContactClick} variant="outline" className="w-full">
                    Contact Our Team
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CoursePage;
